import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  Dialog,
  Slide,
  IconButton,
  Typography,
  Divider,
  Collapse,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import AttendanceVeiwController from "../../redux/actions/AttendenceApiAction";
import ProtectedImage from "../../components/ProtectedImage";
import { BRAND, toolbarButtonSx } from "../../components/auth/authTheme";
import "../../styles/styles.css";
import "../../styles/layout.css";
import "../../styles/attendance.css";

const legendData = [
  { code: "P", label: "Present", className: "present" },
  { code: "A", label: "Absent", className: "absent" },
  { code: "O", label: "Off Day", className: "offday" },
  { code: "R", label: "Rest Day", className: "restday" },
  { code: "L", label: "Leave", className: "leave" },
  { code: "H", label: "Holiday", className: "holiday" },
  { code: "½", label: "Half Day", className: "halfday" },
  { code: "OD", label: "On Duty", className: "onduty" },
  { code: "D", label: "Deduction", className: "deduction" },
  { code: "?", label: "Status Unknown", className: "unknown" },
  { code: "OT", label: "Overtime", className: "overtime" },
  { code: "E", label: "Early Logout", className: "early" },
];

const STATUS_COPY = {
  A: (name) => ({ title: `${name} was Absent`, sub: "No clock-in recorded for this day." }),
  O: () => ({ title: "Off Day", sub: "Scheduled weekly off." }),
  R: () => ({ title: "Rest Day", sub: "Scheduled rest day." }),
  L: (name) => ({ title: `${name} is on Leave`, sub: "Approved leave for this day." }),
  P: () => ({ title: "Present", sub: "Attendance recorded for this day." }),
  H: (name, detail) => ({
    title: detail || "Holiday",
    sub: "Company holiday — no attendance required.",
  }),
  E: () => ({ title: "Early Logout", sub: "Employee logged out before shift end." }),
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AttendanceView = ({ show, handleClose, selectedEmployee }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [legendOpen, setLegendOpen] = useState(false);

  const employeeId = selectedEmployee?.id;
  const { getMonthlyAttendance } = AttendanceVeiwController();
  const { monthly_summary } = useSelector((state) => state.TimeAttendance);
  const dispatch = useDispatch();

  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const currentDate = new Date(selectedYear, selectedMonth - 1);

  useEffect(() => {
    if (employeeId && show) {
      dispatch(getMonthlyAttendance(employeeId, selectedYear, selectedMonth));
    }
  }, [dispatch, employeeId, selectedMonth, selectedYear, show]);

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const statusMap = useMemo(() => {
    const map = {};
    monthly_summary?.attendance_records?.forEach((record) => {
      map[record.date] = {
        code: record.status,
        detail: record.holiday_details,
      };
    });
    return map;
  }, [monthly_summary]);

  const getMonthName = (date) =>
    date.toLocaleString("default", { month: "long", year: "numeric" });

  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();

  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear((prev) => prev - 1);
    } else {
      setSelectedMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear((prev) => prev + 1);
    } else {
      setSelectedMonth((prev) => prev + 1);
    }
  };

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const dateKey = selectedDate.toLocaleDateString("en-CA");
  const statusEntry = statusMap[dateKey];
  const legendClass =
    legendData.find((l) => l.code === statusEntry?.code)?.className || "unknown";

  const attendanceRecord = monthly_summary?.attendance_records?.find(
    (record) => record.date === dateKey
  );

  const clockIn = attendanceRecord?.clock_in ?? null;
  const clockOut = attendanceRecord?.clock_out ?? null;
  const regularHours = attendanceRecord?.regular_hours ?? null;
  const overtimeHours = attendanceRecord?.overtime_hours ?? null;
  const sessions = attendanceRecord?.sessions || [];

  const shiftInfo = attendanceRecord?.shift ?? {
    name: "General",
    start_time: "09:00",
    end_time: "18:00"
  };

  const statusMessage = (() => {
    const code = statusEntry?.code;
    const firstName = selectedEmployee?.first_name || "Employee";
    if (!code) {
      return { title: "No attendance record", sub: "No status logged for this date." };
    }
    const fn = STATUS_COPY[code];
    if (fn) return fn(firstName, statusEntry?.detail);
    return { title: `Status: ${code}`, sub: "See legend for details." };
  })();

  const monthStats = [
    { label: "Present Days", value: monthly_summary?.present_days ?? "-" },
    { label: "Absent Days", value: monthly_summary?.absent_days ?? "-" },
    { label: "Leave Days", value: monthly_summary?.leave_days ?? "-" },
    { label: "Avg Work Hrs", value: monthly_summary?.average_work_hours ?? "-" },
  ];

  const employeeName = `${selectedEmployee?.first_name || ""} ${selectedEmployee?.last_name || ""}`.trim();

  const header = (
    <Box
      className="attendance-view-header"
      sx={{
        px: isMobile ? 1 : 2.5,
        py: isMobile ? 1.25 : 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        borderBottom: `1px solid ${BRAND.border}`,
        bgcolor: "#fff",
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 2, minWidth: 0, flex: 1 }}>
        {isMobile && (
          <IconButton onClick={handleClose} aria-label="Back" edge="start" sx={{ color: BRAND.navy }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        {selectedEmployee?.profile_image ? (
          <ProtectedImage
            path={selectedEmployee.profile_image}
            alt="Profile"
            style={{
              width: isMobile ? 40 : 48,
              height: isMobile ? 40 : 48,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              width: isMobile ? 40 : 48,
              height: isMobile ? 40 : 48,
              borderRadius: "50%",
              bgcolor: BRAND.navy,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: isMobile ? "0.875rem" : "1rem",
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            {selectedEmployee?.first_name?.[0]}
            {selectedEmployee?.last_name?.[0]}
          </Box>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: BRAND.navy,
                fontSize: isMobile ? "0.9375rem" : "1.25rem",
                lineHeight: 1.2,
              }}
            >
              {employeeName || "Employee"}
            </Typography>
            {!isMobile && (
              <Typography variant="body2" sx={{ color: BRAND.muted }}>
                ({selectedEmployee?.employee_id || "N/A"})
              </Typography>
            )}
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: selectedEmployee?.is_active ? "#16a34a" : "#dc2626",
                flexShrink: 0,
              }}
            />
          </Box>
          <Typography
            sx={{
              color: BRAND.muted,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              lineHeight: 1.3,
            }}
          >
            {isMobile && selectedEmployee?.employee_id
              ? `${selectedEmployee.employee_id} · `
              : ""}
            {selectedEmployee?.designation ||
              selectedEmployee?.profile_data?.designation_name ||
              "Designation not set"}
          </Typography>
        </Box>
      </Box>

      {!isMobile && (
        <IconButton onClick={handleClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );

  const body = (
    <Box className={`attendance-drawer-body${isMobile ? " attendance-drawer-body-mobile" : ""}`}>
      {selectedEmployee && (
        <>
          <div className="attendance-month-stats">
            {monthStats.map((item) => (
              <div className="attendance-month-stat" key={item.label}>
                <p className="attendance-month-stat-label">{item.label}</p>
                <p className="attendance-month-stat-value">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="attendance-view-layout">
            <div className="attendance-panel">
              <div className="attendance-calendar-nav">
                {isMobile ? (
                  <>
                    <IconButton
                      size="small"
                      onClick={handlePrevMonth}
                      aria-label="Previous month"
                      sx={{
                        border: `1px solid ${BRAND.orange}`,
                        color: BRAND.orange,
                        borderRadius: "10px",
                        width: 36,
                        height: 36,
                      }}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <h4 className="attendance-calendar-month">
                      {getMonthName(currentDate)}
                    </h4>
                    <IconButton
                      size="small"
                      onClick={handleNextMonth}
                      aria-label="Next month"
                      sx={{
                        border: `1px solid ${BRAND.orange}`,
                        color: BRAND.orange,
                        borderRadius: "10px",
                        width: 36,
                        height: 36,
                      }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ChevronLeftIcon />}
                      onClick={handlePrevMonth}
                      sx={{
                        ...toolbarButtonSx,
                        bgcolor: "#fff",
                        color: BRAND.orange,
                        borderColor: BRAND.orange,
                        "&:hover": { bgcolor: BRAND.orangeLight, borderColor: BRAND.orange },
                      }}
                    >
                      Prev
                    </Button>
                    <h4 className="attendance-calendar-month">
                      {getMonthName(currentDate)}
                    </h4>
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<ChevronRightIcon />}
                      onClick={handleNextMonth}
                      sx={{
                        ...toolbarButtonSx,
                        bgcolor: "#fff",
                        color: BRAND.orange,
                        borderColor: BRAND.orange,
                        "&:hover": { bgcolor: BRAND.orangeLight, borderColor: BRAND.orange },
                      }}
                    >
                      Next
                    </Button>
                  </>
                )}
              </div>

              <div className="attendance-calendar-grid">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                  <div className="attendance-calendar-weekday" key={day}>
                    {isMobile ? day.charAt(0) : day}
                  </div>
                ))}

                {Array(
                  getFirstDayOfMonth(
                    currentDate.getMonth(),
                    currentDate.getFullYear()
                  )
                )
                  .fill(null)
                  .map((_, i) => (
                    <div
                      key={`empty-${i}`}
                      className="attendance-calendar-cell empty"
                    />
                  ))}

                {Array(
                  daysInMonth(
                    currentDate.getMonth(),
                    currentDate.getFullYear()
                  )
                )
                  .fill(null)
                  .map((_, i) => {
                    const date = i + 1;
                    const cellDate = new Date(
                      currentDate.getFullYear(),
                      currentDate.getMonth(),
                      date
                    );
                    const fullDate = cellDate.toLocaleDateString("en-CA");
                    const status = statusMap[fullDate];
                    const cellClass =
                      legendData.find((l) => l.code === status?.code)
                        ?.className || "";

                    return (
                      <div
                        key={date}
                        className={[
                          "attendance-calendar-cell",
                          cellClass,
                          isSameDay(cellDate, selectedDate) ? "is-selected" : "",
                          isSameDay(cellDate, today) ? "is-today" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => setSelectedDate(cellDate)}
                        style={
                          cellClass
                            ? {
                                background:
                                  cellClass === "present"
                                    ? "#f0fdf4"
                                    : cellClass === "absent"
                                      ? "#fef2f2"
                                      : cellClass === "offday"
                                        ? "#f7fee7"
                                        : cellClass === "restday"
                                          ? "#ecfeff"
                                          : undefined,
                              }
                            : undefined
                        }
                      >
                        <span className="attendance-calendar-date">{date}</span>
                        {status?.code && (
                          <span
                            className={`attendance-calendar-code legend-symbol ${cellClass}`}
                          >
                            {status.code}
                          </span>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <div className="attendance-panel">
                <h5 className="attendance-panel-title">Day Details</h5>

                <div className="attendance-day-summary">
                  <div className="attendance-day-date-block">
                    <div className="attendance-day-date-num">
                      {selectedDate.getDate()}
                    </div>
                    <div className="attendance-day-date-weekday">
                      {selectedDate.toLocaleString("en-us", { weekday: "short" })}
                    </div>
                  </div>
                  <div>
                    <p className="attendance-day-meta-title">{shiftInfo.name}</p>
                    <p className="attendance-day-meta-sub">Shift: {shiftInfo.start_time} to {shiftInfo.end_time}</p>
                  </div>
                </div>

                <div className={`attendance-status-banner ${legendClass}`}>
                  <strong>{statusMessage.title}</strong>
                  <span>{statusMessage.sub}</span>
                </div>

                {clockIn ? (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <div className="attendance-clock-grid">
                      <div className="attendance-clock-item">
                        <p className="attendance-clock-item-label">Clock In</p>
                        <p className="attendance-clock-item-value">
                          {formatTime(clockIn)}
                        </p>
                      </div>
                      <div className="attendance-clock-item">
                        <p className="attendance-clock-item-label">Clock Out</p>
                        <p className="attendance-clock-item-value">
                          {clockOut ? formatTime(clockOut) : "-"}
                        </p>
                      </div>
                      <div className="attendance-clock-item">
                        <p className="attendance-clock-item-label">Regular Hours</p>
                        <p className="attendance-clock-item-value">
                          {regularHours ?? "-"}
                        </p>
                      </div>
                      <div className="attendance-clock-item">
                        <p className="attendance-clock-item-label">Overtime Hours</p>
                        <p className="attendance-clock-item-value">
                          {overtimeHours ?? "-"}
                        </p>
                      </div>
                    </div>
                  </>
                ) : null}

                {/* Punch Log Sessions */}
                {sessions.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: BRAND.navy, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>🕐</span> Punch Log
                      </Typography>
                      <span style={{ fontSize: '0.72rem', background: '#f0f4ff', color: '#3b5bdb', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                        {sessions.length} session{sessions.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {sessions.map((s, idx) => {
                        const isOpen = s.clock_in && !s.clock_out;
                        return (
                          <div key={idx} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            fontSize: '0.78rem'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{
                                width: 20, height: 20, borderRadius: '50%',
                                backgroundColor: isOpen ? '#fff3e0' : '#e8f5e9',
                                color: isOpen ? '#e65100' : '#2e7d32',
                                border: `1px solid ${isOpen ? '#ff9800' : '#4caf50'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: '0.68rem', flexShrink: 0
                              }}>
                                {idx + 1}
                              </span>
                              <span>
                                <strong>In:</strong> {s.clock_in ? formatTime(s.clock_in) : '-'}
                              </span>
                              <span style={{ color: '#cbd5e1' }}>|</span>
                              <span>
                                <strong>Out:</strong> {s.clock_out ? formatTime(s.clock_out) : <span style={{ color: '#ff9800', fontWeight: 600 }}>⏳ On Duty</span>}
                              </span>
                            </div>
                            {s.duration_hours != null && (
                              <span style={{ fontWeight: 600, color: '#3b5bdb', backgroundColor: '#eff6ff', padding: '2px 6px', borderRadius: 4, fontSize: '0.75rem' }}>
                                {s.duration_hours}h
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              <div className="attendance-panel attendance-legend-panel">
                <button
                  type="button"
                  className="attendance-legend-toggle"
                  onClick={() => setLegendOpen((prev) => !prev)}
                  aria-expanded={legendOpen}
                >
                  <h5 className="attendance-panel-title">Legends</h5>
                  {isMobile && (
                    <ExpandMoreIcon
                      sx={{
                        color: BRAND.muted,
                        transform: legendOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s",
                      }}
                    />
                  )}
                </button>
                <Collapse in={!isMobile || legendOpen}>
                  <div className="attendance-legend-grid">
                    {legendData.map((item) => (
                      <div className="attendance-legend-item" key={item.code}>
                        <span className={`legend-symbol ${item.className}`}>
                          {item.code}
                        </span>
                        <span className="attendance-legend-label">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </Collapse>
              </div>
            </Box>
          </div>
        </>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Dialog
        fullScreen
        open={show}
        onClose={handleClose}
        TransitionComponent={Transition}
        className="attendance-view-drawer attendance-view-mobile"
        PaperProps={{
          sx: {
            borderRadius: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: BRAND.bg,
          },
        }}
      >
        {header}
        {body}
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={show}
      onClose={handleClose}
      className="attendance-view-drawer"
      PaperProps={{
        sx: {
          width: { xs: "100%", md: 1080 },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {header}
      {body}
    </Drawer>
  );
};

export default AttendanceView;
